import React, { useState, useEffect, useRef } from "react";
import { Form, InputGroup } from "react-bootstrap";
import { useCurrency } from "../../../../_helper/CurrencyContext";
import { useTranslation } from "react-i18next";

const CurrencyAmountInput = ({
    label = "Amount",
    amount,
    onAmountChange,
    onCurrencyChange,
    disabled = false,
    errors = {},
    disableScrollProps = {},
    defaultCurrency,
    originalAmount,
    paidInCurrency,
    settlementData
}) => {
    const {
        currency: defaultCurrencyFromContext,
        allowedCurrencies,
        getCurrencySymbol,
        convertCurrency
    } = useCurrency();
    const { t } = useTranslation();

    const baseCurrency = defaultCurrency || defaultCurrencyFromContext;
    const [localCurrency, setLocalCurrency] = useState(paidInCurrency || baseCurrency);
    const [inputValue, setInputValue] = useState("");
    const inputRef = useRef(null);
    const originalAmountRef = useRef(originalAmount ?? amount);

    // Initialize input value
    useEffect(() => {
        if (amount !== undefined && amount !== null) {
            const formatted = formatDisplayValue(amount);
            setInputValue(formatted);
        } else {
            setInputValue("");
        }
    }, []);

    // Sync with external amount changes
    useEffect(() => {
        if (amount !== undefined && amount !== null) {
            const formatted = formatDisplayValue(amount);
            if (inputValue !== formatted) {
                setInputValue(formatted);
            }
        }
    }, [amount]);

    // Keep track of original amount
    useEffect(() => {
        if (originalAmount !== undefined) {
            originalAmountRef.current = originalAmount;
        }
    }, [originalAmount]);

    // Handle currency changes
    useEffect(() => {
        setLocalCurrency(paidInCurrency || baseCurrency);
    }, [paidInCurrency, baseCurrency]);

    const formatDisplayValue = (value) => {
        if (value === undefined || value === null || value === "") return "";
        const num = parseFloat(Number(value).toFixed(2));
        if (isNaN(num)) return "";

        // Remove trailing zeros in decimal part if it's .00
        const str = num.toString();
        if (str.endsWith('.00')) {
            return str.slice(0, -3);
        }
        return str;
    };

    const calculateMaxAmount = () => {
        if (originalAmount === undefined || originalAmount === null) return null;

        // If settlementData has pre-calculated balances for this specific currency, trust those first
        // This ensures alignment between frontend display and backend expectations
        if (settlementData?.remainingAmounts && settlementData.remainingAmounts[localCurrency] !== undefined) {
            const val = settlementData.remainingAmounts[localCurrency];
            return typeof val === 'number' ? val : parseFloat(val) || 0;
        }

        if (localCurrency === baseCurrency) {
            return originalAmount;
        }

            try {
                const converted = convertCurrency(originalAmount, baseCurrency, localCurrency);
                // Keep rounding consistent with the rest of the UI (toFixed(2))
                // so "Max Payable" matches "Total Amount" and "Payment Amount" previews.
                return Math.round(converted * 100) / 100;
            } catch (error) {
            console.error("Conversion error:", error);
            return originalAmount;
        }
    };

    const handleInputChange = (value) => {
        // Allow empty string
        if (value === "") {
            setInputValue("");
            onAmountChange(0);
            return;
        }

        // Allow only numbers and one decimal point
        const regex = /^(\d*)(\.\d{0,2})?$/;
        if (!regex.test(value)) return;

        // Don't allow leading zeros unless it's "0." or "0"
        if (value.length > 1 && value[0] === '0' && value[1] !== '.') {
            value = value.substring(1);
        }

        setInputValue(value);

        const numericValue = value === "" ? 0 : parseFloat(value);
        if (!isNaN(numericValue)) {
            const maxAmount = calculateMaxAmount();
            let finalValue = Math.max(0, numericValue);

            if (maxAmount !== null && finalValue > maxAmount) {
                finalValue = maxAmount;
                const formattedMax = formatDisplayValue(maxAmount);
                setInputValue(formattedMax);
            }

            onAmountChange(parseFloat(finalValue.toFixed(2)));
        }
    };

    const handleBlur = () => {
        if (inputValue === "") {
            setInputValue("0");
            onAmountChange(0);
            return;
        }

        // Ensure we have proper decimal format
        let value = inputValue;
        if (value === ".") {
            value = "0.00";
        } else if (!value.includes('.')) {
            value = value + ".00";
        } else if (value.endsWith('.')) {
            value = value + "00";
        } else if (value.split('.')[1].length === 1) {
            value = value + "0";
        }

        const numericValue = parseFloat(value);
        if (!isNaN(numericValue)) {
            const maxAmount = calculateMaxAmount();
            let finalValue = numericValue;

            if (maxAmount !== null && finalValue > maxAmount) {
                finalValue = maxAmount;
            }

            const formattedValue = formatDisplayValue(finalValue);
            setInputValue(formattedValue);
            onAmountChange(parseFloat(finalValue.toFixed(2)));
        }
    };

    const handleFocus = (e) => {
        // Select all text on focus for easy editing
        e.target.select();
    };

    const handleCurrencyChange = (newCurrency) => {
        if (newCurrency === localCurrency) return;

        const currentValue = inputValue === "" ? 0 : parseFloat(inputValue);

        if (settlementData?.remainingAmounts) {
            const remaining = settlementData.remainingAmounts[newCurrency];
            if (remaining !== undefined) {
                const newAmount = parseFloat(remaining.toFixed(3));
                const formatted = formatDisplayValue(newAmount);
                setInputValue(formatted);
                onAmountChange(newAmount);
                setLocalCurrency(newCurrency);
                onCurrencyChange?.(newCurrency);
                return;
            }
        }

        let newAmount;
        if (newCurrency === baseCurrency) {
            // Switching back to base currency
            newAmount = originalAmountRef.current || 0;
        } else {
            // Converting between currencies
            newAmount = convertCurrency(currentValue, localCurrency, newCurrency);
        }

        const finalAmount = parseFloat(newAmount.toFixed(3));
        const formatted = formatDisplayValue(finalAmount);
        setInputValue(formatted);
        onAmountChange(finalAmount);
        setLocalCurrency(newCurrency);
        onCurrencyChange?.(newCurrency);
    };

    const maxAmount = calculateMaxAmount();
    const displayMaxAmount = maxAmount !== null ? maxAmount.toFixed(2) : "0.00";

    return (
        <Form.Group className="mb-3">
            <Form.Label>{label}</Form.Label>

            <InputGroup>
                <Form.Control
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={(e) => handleInputChange(e.target.value)}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                    disabled={disabled}
                    isInvalid={!!errors.amount}
                    placeholder="0"
                    {...disableScrollProps}
                />

                <Form.Select
                    value={localCurrency}
                    style={{ maxWidth: "110px" }}
                    onChange={(e) => handleCurrencyChange(e.target.value)}

                >
                    {allowedCurrencies.map((cur) => (
                        <option key={cur} value={cur}>
                            {cur}
                        </option>
                    ))}
                </Form.Select>

                <Form.Control.Feedback type="invalid">
                    {errors.amount}
                </Form.Control.Feedback>
            </InputGroup>

            {maxAmount !== null && (
                <Form.Text className="text-muted">
                    {t("Max Payable Amt")}: {getCurrencySymbol(localCurrency)}{displayMaxAmount}
                </Form.Text>
            )}
            <br />
            {localCurrency !== baseCurrency && originalAmountRef.current && (
                <Form.Text className="text-muted">
                    {t("Max Payable Amt base currency")}: {getCurrencySymbol(baseCurrency)}{originalAmountRef.current.toFixed(2)}
                </Form.Text>
            )}
        </Form.Group>
    );
};

export default CurrencyAmountInput;